import { useMutation, useQueryClient } from '@tanstack/react-query';
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import apiClient from './apiClient';

const useAuthStore = create(
  persist(
    (set, get) => ({
      token: null,
      refreshToken: null,
      expirationTime: null,
      isAuthenticated: false,
      user: null,
      hydrated: false,

      setAuth: (token, expirationTime, user, refreshToken = null) =>
        set({
          token,
          refreshToken,
          expirationTime,
          isAuthenticated: true,
          user,
        }),

      clearAuth: () =>
        set({
          token: null,
          refreshToken: null,
          expirationTime: null,
          isAuthenticated: false,
          user: null,
        }),

      isTokenExpired: () => {
        const { expirationTime } = get();
        if (!expirationTime) return true;
        return new Date() >= new Date(expirationTime);
      },

      setHydrated: (value) => set({ hydrated: value }),
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      onRehydrateStorage: () => (state) => {
        state?.setHydrated?.(true);
      },
    }
  )
);

export const useAuth = () => {
  const queryClient = useQueryClient();
  const {
    token,
    expirationTime,
    isAuthenticated,
    user,
    setAuth,
    clearAuth,
    isTokenExpired,
    hydrated,
  } = useAuthStore();

  const loginMutation = useMutation({
    mutationFn: async ({ email, password }) => {
      const response = await apiClient.post('auth/login', { email, password });
      return {
        data: response.data,
        status: response.status,
      };
    },
    onSuccess: (response) => {
      const { jwt, expiresIn, user, refreshJwt } = response.data;
      const expirationTime = Date.now() + expiresIn * 1000;
      setAuth(jwt, expirationTime, user, refreshJwt || null);
      queryClient.invalidateQueries();
    },
    onError: (error) => {
      clearAuth();
      console.error('Login failed:', error);
    },
  });

  const logoutMutation = useMutation({
    mutationFn: async () => {
      await apiClient.post('auth/logout');
    },
    onSuccess: () => {
      clearAuth();
      queryClient.clear();
    },
    onSettled: () => {
      clearAuth();
      queryClient.clear();
    },
  });

  const extendSessionMutation = useMutation({
    mutationFn: async () => {
      let refreshToken = useAuthStore.getState().refreshToken;

      if (!refreshToken) {
        const raw = localStorage.getItem('auth-storage');
        if (raw) {
          const parsed = JSON.parse(raw);
          refreshToken = parsed?.state?.refreshToken || parsed?.state?.refreshJwt || null;
        }
      }

      if (!refreshToken) throw new Error('No refresh token available');

      const resp = await apiClient.post('auth/extend_session', { refreshToken });
      const data = resp.data;

      const newJwt = data.jwt || data?.data?.jwt || data?.token;
      const newRefresh = data.refreshJwt || data?.data?.refreshJwt || refreshToken;
      const expiresIn = data.expiresIn || data?.data?.expiresIn || 3600;

      const newExpirationTime = Date.now() + expiresIn * 1000;

      setAuth(newJwt, newExpirationTime, user, newRefresh || refreshToken);

      return { expirationTime: newExpirationTime };
    },
  });

  const passwordRecoveryMutation = useMutation({
    mutationFn: async ({ email }) => {
      const response = await apiClient.post('auth/recover_password', { email });
      return {
        data: response.data,
        status: response.status,
      };
    },
    onError: (error) => {
      console.error('Password recovery failed:', error);
    },
  });

  const login = (credentials) => loginMutation.mutateAsync(credentials);
  const logout = () => logoutMutation.mutate();
  const extendSession = () => extendSessionMutation.mutateAsync();
  const recoverPassword = ({ email }) => passwordRecoveryMutation.mutateAsync({ email }).then((result) => result.data);
  const clearToken = () => clearAuth();

  return {
    token,
    expirationTime,
    isAuthenticated: isAuthenticated && !isTokenExpired(),
    isHydrated: hydrated,
    isLoading:
      loginMutation.isPending ||
      logoutMutation.isPending ||
      extendSessionMutation.isPending ||
      passwordRecoveryMutation.isPending,
    error:
      loginMutation.error ||
      logoutMutation.error ||
      extendSessionMutation.error ||
      passwordRecoveryMutation.error,
    user,
    login,
    logout,
    extendSession,
    recoverPassword,
    clearAuth,
    clearToken,
    setAuth,
    isRecoveringPassword: passwordRecoveryMutation.isPending,
    passwordRecoveryResponse: passwordRecoveryMutation.data?.data,
  };
};
